import { toast } from "react-hot-toast";
import {
  MdCheckCircle,
  MdError,
  MdWarning,
  MdInfo,
  MdEdit,
} from "react-icons/md";
import { BiLoaderAlt } from "react-icons/bi";

// Compact & Professional Toast Style
const toastStyle = {
  background: "#fff",
  color: "#333",
  padding: "8px 12px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  border: "1px solid #e5e7eb",
  minWidth: "250px",
  maxWidth: "420px",
};

// Compact Loader
const Loader = () => (
  <div className="flex items-center justify-center">
    <BiLoaderAlt className="w-4 h-4 text-blue-600 animate-spin" />
  </div>
);

// SUCCESS
export const showSuccessToast = (message, options = {}) => {
  return toast(
    () => (
      <div className="flex items-center space-x-2">
        <MdCheckCircle className="w-4 h-4 text-green-500" />
        <span className="text-xs font-medium">{message}</span>
      </div>
    ),
    {
      style: {
        ...toastStyle,
        borderLeft: "3px solid #10b981",
      },
      duration: options.duration || 3000,
      position: options.position || "top-center",
      ...options,
    }
  );
};

// ERROR
export const showErrorToast = (message, options = {}) => {
  return toast(
    () => (
      <div className="flex items-center space-x-2">
        <MdError className="w-4 h-4 text-red-500" />
        <span className="text-xs font-medium">{message}</span>
      </div>
    ),
    {
      style: {
        ...toastStyle,
        borderLeft: "3px solid #ef4444",
      },
      duration: options.duration || 3500,
      position: "top-center",
      ...options,
    }
  );
};

// WARNING
export const showWarningToast = (message, options = {}) => {
  return toast(
    () => (
      <div className="flex items-center space-x-2">
        <MdWarning className="w-4 h-4 text-yellow-500" />
        <span className="text-xs font-medium">{message}</span>
      </div>
    ),
    {
      style: {
        ...toastStyle,
        borderLeft: "3px solid #f59e0b",
      },
      duration: options.duration || 3200,
      ...options,
    }
  );
};

// INFO
export const showInfoToast = (message, options = {}) => {
  return toast(
    () => (
      <div className="flex items-center space-x-2">
        <MdInfo className="w-4 h-4 text-blue-500" />
        <span className="text-xs font-medium">{message}</span>
      </div>
    ),
    {
      style: {
        ...toastStyle,
        borderLeft: "3px solid #3b82f6",
      },
      duration: options.duration || 3200,
      ...options,
    }
  );
};

// EDIT INFO TOAST
export const showEditInfoToast = (patientName, options = {}) => {
  const message = `Edit appointment for ${patientName}`;

  return toast(
    () => (
      <div className="flex items-center space-x-2 cursor-pointer">
        <MdEdit className="w-4 h-4 text-purple-500" />
        <div className="flex flex-col leading-tight">
          <span className="text-xs font-medium">{message}</span>
          <span className="text-[10px] text-gray-500">Click to edit</span>
        </div>
      </div>
    ),
    {
      style: {
        ...toastStyle,
        borderLeft: "3px solid #8b5cf6",
        cursor: "pointer",
      },
      onClick: options.onClick,
      duration: 3000,
      ...options,
    }
  );
};

// LOADING
export const showLoadingToast = (message, options = {}) => {
  return toast(
    () => (
      <div className="flex items-center space-x-2">
        <Loader />
        <span className="text-xs font-medium">{message}</span>
      </div>
    ),
    {
      style: {
        ...toastStyle,
        borderLeft: "3px solid #6b7280",
      },
      duration: Infinity,
      ...options,
    }
  );
};

// PROMISE
export const showToastPromise = (promise, messages, options = {}) => {
  const { loading, success, error } = messages;

  return toast.promise(
    promise,
    {
      loading: (
        <div className="flex items-center space-x-2">
          <Loader />
          <span className="text-xs font-medium">{loading}</span>
        </div>
      ),
      success: (data) => (
        <div className="flex items-center space-x-2">
          <span className="text-xs font-medium">
            {typeof success === "function" ? success(data) : success}
          </span>
        </div>
      ),
      error: (err) => (
        <div className="flex items-center space-x-2">
          <span className="text-xs font-medium">
            {typeof error === "function" ? error(err) : error}
          </span>
        </div>
      ),
    },
    {
      style: toastStyle,
      position: "top-center",
      ...options,
    }
  );
};

// CONFIRMATION
export const showConfirmationToast = (
  message,
  onConfirm,
  onCancel,
  options = {}
) => {
  return toast(
    (t) => (
      <div className="flex flex-col space-y-2">
        <div className="flex items-center space-x-2">
          <MdWarning className="w-4 h-4 text-yellow-500" />
          <span className="text-xs font-medium">{message}</span>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => {
              onCancel?.();
              toast.dismiss(t.id);
            }}
            className="px-2 py-1 text-[11px] bg-gray-200 rounded"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm?.();
              toast.dismiss(t.id);
            }}
            className="px-2 py-1 text-[11px] bg-red-500 text-white rounded"
          >
            Confirm
          </button>
        </div>
      </div>
    ),
    {
      style: {
        ...toastStyle,
        borderLeft: "3px solid #f59e0b",
      },
      duration: Infinity,
      ...options,
    }
  );
};

// Dismiss all
export const dismissAllToasts = () => toast.dismiss();
export const dismissToast = (id) => toast.dismiss(id);

// Custom Component
export const showCustomToast = (component, options = {}) => {
  return toast(component, {
    style: toastStyle,
    duration: 3000,
    ...options,
  });
};
